import type { ChangeEvent } from "react";
import type { PianoRollAppearance as PianoRollAppearanceValue, PianoRollColors } from "../config.js";
import { defaultPianoRollColors } from "../config.js";

type PianoRollAppearanceProps = {
  value: PianoRollAppearanceValue;
  onChange: (next: PianoRollAppearanceValue) => void;
};

type ColorKey = keyof PianoRollColors;

const COLOR_FIELDS: ReadonlyArray<{ key: ColorKey; label: string }> = [
  { key: "gridStrong", label: "Сетка: сильные" },
  { key: "gridMed", label: "Сетка: средние" },
  { key: "gridFaint", label: "Сетка: слабые" },
  { key: "gridSub", label: "Сетка: подсетка" },
  { key: "keyWhite", label: "Клавиши: белые" },
  { key: "keyBlack", label: "Клавиши: чёрные" },
  { key: "keyBorder", label: "Клавиши: граница" },
  { key: "keyLabel", label: "Клавиши: подпись" },
];

const isHex = (v: string): boolean => /^#[0-9a-fA-F]{6}$/.test(v);

/** Приводит CSS-цвет (в т.ч. rgba/rgb) к #rrggbb для <input type="color">. */
const cssColorToHex = (v: string): string => {
  if (isHex(v)) return v;
  const m = v.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (m) {
    const toHex = (n: string): string =>
      Math.max(0, Math.min(255, Number(n))).toString(16).padStart(2, "0");
    return `#${toHex(m[1]!)}${toHex(m[2]!)}${toHex(m[3]!)}`;
  }
  return "#ffffff";
};

export const PianoRollAppearance = ({ value, onChange }: PianoRollAppearanceProps) => {
  const colors = value.colors ?? defaultPianoRollColors();

  const setColor = (key: ColorKey) => (e: ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...value,
      colors: { ...colors, [key]: e.target.value },
    });
  };

  const setHeight = (e: ChangeEvent<HTMLInputElement>) => {
    const h = Number(e.target.value);
    onChange({ ...value, height: Number.isFinite(h) ? Math.round(h) : value.height });
  };

  return (
    <div className="settings-section">
      <p className="muted">Внешний вид таймлайна (piano roll).</p>
      {COLOR_FIELDS.map(({ key, label }) => (
        <label className="settings-row" key={key}>
          <span className="settings-row__label">{label}</span>
          <input
            type="color"
            className="settings-row__input"
            style={{ width: 48, height: 28, padding: 2 }}
            value={cssColorToHex(colors[key])}
            onChange={setColor(key)}
            aria-label={label}
          />
          <span className="muted" style={{ fontSize: 11 }}>
            {colors[key]}
          </span>
        </label>
      ))}
      <label className="settings-row">
        <span className="settings-row__label">Высота (px)</span>
        <input
          type="number"
          className="settings-row__input"
          min={80}
          max={2000}
          value={value.height}
          onChange={setHeight}
          aria-label="Высота piano roll"
        />
      </label>
    </div>
  );
};
